/**
 * DatePicker Component
 * 
 * Date selection with various formats and configurations.
 * Built on MUI DatePicker with HAAT styling.
 */

import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import type { TimeView } from '@mui/x-date-pickers/models';

// =============================================================================
// TYPES
// =============================================================================

export interface DatePickerProps {
  /** Field label */
  label: string;
  /** Selected date value */
  value: Date | null;
  /** Change handler */
  onChange: (date: Date | null) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Read-only state */
  readOnly?: boolean;
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Helper text */
  helperText?: string;
  /** Required field */
  required?: boolean;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Disable specific dates (e.g. weekends). Return true to disable the date. */
  shouldDisableDate?: (date: Date) => boolean;
  /** Disable specific times per view (e.g. busy slots). Return true to disable. */
  shouldDisableTime?: (value: Date, view: TimeView) => boolean;
  /** Minimum selectable time (only the time-of-day part is used; time/datetime pickers) */
  minTime?: Date;
  /** Maximum selectable time (only the time-of-day part is used; time/datetime pickers) */
  maxTime?: Date;
  /** Show 12-hour AM/PM clock. Defaults to false (24-hour) for time/datetime pickers. */
  ampm?: boolean;
  /** Hide (instead of just disabling) clock times outside minTime/maxTime. */
  skipDisabled?: boolean;
  /** Date format */
  format?: string;
  /** Full width */
  fullWidth?: boolean;
  /** Picker type */
  type?: 'date' | 'time' | 'datetime';
  /** Size variant */
  size?: 'small' | 'medium';
  /** Custom width */
  width?: string | number;
  /** Additional class name */
  className?: string;
  /** Custom input padding (e.g., '10px 14px' for vertical horizontal) */
  inputPadding?: string;
  /** Custom input font size (e.g., '14px') */
  inputFontSize?: string;
  /** Show a button that clears the current value */
  clearable?: boolean;
}

const isValidDate = (date: Date | null | undefined): date is Date =>
  date instanceof Date && !Number.isNaN(date.getTime());

// =============================================================================
// COMPONENT
// =============================================================================

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  readOnly = false,
  error = false,
  errorMessage,
  helperText,
  required = false,
  minDate,
  maxDate,
  shouldDisableDate,
  shouldDisableTime,
  minTime,
  maxTime,
  ampm,
  skipDisabled,
  format,
  fullWidth = true,
  type = 'date',
  size = 'medium',
  width,
  className = '',
  inputPadding,
  inputFontSize,
  clearable = false,
}) => {
  const hasError = error || !!errorMessage;
  const displayHelperText = errorMessage || helperText;
  const resolvedFontSize = inputFontSize || (size === 'small' ? '0.875rem' : '1rem');
  const resolvedPadding = inputPadding || (size === 'small' ? '8px 12px' : '16.5px 14px');
  const normalizedValue = isValidDate(value) ? value : null;
  const handleChange = (date: Date | null) => {
    onChange(isValidDate(date) ? date : null);
  };

  // Common styles for all picker types
  const commonSx = {
    width: width || (fullWidth ? '100%' : 'auto'),

    // Root wrappers
    '& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiPickersInputBase-root': {
      borderRadius: '12px',
      minHeight: size === 'small' ? '36px' : '42px',
      height: size === 'small' ? '36px' : '42px',
      backgroundColor: 'white',
      transition: 'all 0.2s ease-in-out',
      boxSizing: 'border-box',
    },

    // Text input padding
    '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
      padding: resolvedPadding,
      height: 'auto',
      lineHeight: '1.25rem',
      fontSize: resolvedFontSize,
      boxSizing: 'border-box',
    },

    // New pickers section list padding (MUI x-date-pickers)
    '& .MuiPickersSectionList-root': {
      padding: resolvedPadding,
      minHeight: size === 'small' ? '36px' : '42px',
      boxSizing: 'border-box',
      alignItems: 'center',
    },
    '& .MuiPickersInputBase-sectionsContainer': {
      padding: resolvedPadding,
      minHeight: size === 'small' ? '36px' : '42px',
      boxSizing: 'border-box',
      alignItems: 'center',
    },
    '& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionContent': {
      fontSize: resolvedFontSize,
      lineHeight: '1.25rem',
    },

    '& fieldset': {
      borderColor: hasError ? '#c00035' : '#e5e7eb',
      transition: 'border-color 0.2s ease-in-out',
    },

    '&:hover fieldset': {
      borderColor: hasError ? '#c00035' : '#9ca3af',
    },

    '&.Mui-focused fieldset': {
      borderColor: hasError ? '#c00035' : 'rgb(var(--color-primary-500))',
      borderWidth: '2px',
    },

    '&.Mui-disabled': {
      backgroundColor: '#f9fafb',
      '& fieldset': {
        borderColor: '#e5e7eb',
      },
    },

    '& .MuiInputLabel-root': {
      color: hasError ? '#c00035' : '#6b7280',

      '&.Mui-focused': {
        color: hasError ? '#c00035' : 'rgb(var(--color-primary-500))',
      },
    },

    '& .MuiInputBase-input': {
      fontSize: resolvedFontSize,
      '&::placeholder': {
        color: '#9ca3af',
        opacity: 1,
      },
    },

    '& .MuiFormHelperText-root': {
      marginLeft: '2px',
      marginTop: '6px',
      fontSize: '0.75rem',

      '&.Mui-error': {
        color: '#c00035',
      },
    },

    '& .MuiIconButton-root': {
      color: '#6b7280',

      '&:hover': {
        backgroundColor: 'rgba(var(--color-primary-500), 0.04)',
        color: 'rgb(var(--color-primary-500))',
      },
    },
  };

  // Get the appropriate format based on type
  const getDefaultFormat = () => {
    switch (type) {
      case 'time':
        return 'HH:mm';
      case 'datetime':
        return 'dd/MM/yyyy HH:mm';
      default:
        return 'dd/MM/yyyy';
    }
  };

  const renderPicker = () => {
    const commonProps = {
      label: label || undefined, // Don't show label if empty string
      value: normalizedValue,
      onChange: handleChange,
      disabled,
      readOnly,
      minDate,
      maxDate,
      shouldDisableDate,
      format: format || getDefaultFormat(),
      slotProps: {
        textField: {
          required,
          error: hasError,
          helperText: displayHelperText,
          placeholder,
          size,
          fullWidth,
          sx: {
            ...commonSx,
            '& .MuiInputLabel-root': {
              ...(label ? {} : { display: 'none' }), // Hide label if empty
            },
          },
        },
        ...(clearable ? { field: { clearable: true } } : {}),
        openPickerButton: {
          sx: {
            color: '#6b7280',
            '&:hover': {
              backgroundColor: 'rgba(var(--color-primary-500), 0.04)',
              color: 'rgb(var(--color-primary-500))',
            },
          },
        },
        // Keep calendar popper above custom app modals (z-index 9999)
        popper: {
          sx: {
            zIndex: 10000,
          },
        },
      },
    };

    // Time-of-day constraints and clock format apply only to the time and
    // datetime pickers. Default to a 24-hour clock (no AM/PM) unless overridden.
    const timeProps = {
      ampm: ampm ?? false,
      minTime,
      maxTime,
      skipDisabled,
      shouldDisableTime,
    };

    switch (type) {
      case 'time':
        return <MuiTimePicker {...commonProps} {...timeProps} />;
      case 'datetime':
        return <MuiDateTimePicker {...commonProps} {...timeProps} />;
      default:
        return <MuiDatePicker {...commonProps} />;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className={className}>
        {renderPicker()}
      </div>
    </LocalizationProvider>
  );
};

export default DatePicker;

